import React, { useState, useEffect } from 'react';
import { Table, Button, message, Space, Modal, Input } from 'antd';
import { studentService } from '../../services/studentService';
import { authService } from '../../services/authService';
import { ROLE_LEVELS } from '../../services/authService';

const StudentDocumentation = () => {
  const [loading, setLoading] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const [undocumentedStudents, setUndocumentedStudents] = useState([]);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [fileIdModalVisible, setFileIdModalVisible] = useState(false);
  const [fileId, setFileId] = useState('');
  const [currentStudent, setCurrentStudent] = useState(null);

  // Permission check
  useEffect(() => {
    const checkPermission = async () => {
      try {
        const canAccess = await authService.canAccess(ROLE_LEVELS.MANAGER); // Manager level (2)
        setHasPermission(canAccess);
      } catch (error) {
        console.error('Permission check error:', error);
        message.error('权限检查失败');
      }
    };
    checkPermission();
  }, []);

  // Fetch undocumented students
  const fetchUndocumentedStudents = async () => {
    try {
      setLoading(true);
      const students = await studentService.getUndocumentedStudents();
      setUndocumentedStudents(students);
    } catch (error) {
      message.error('获取未审档学员失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUndocumentedStudents();
  }, []);

  // Handle documentation approval
  const handleApprove = (student) => {
    setCurrentStudent(student);
    setFileIdModalVisible(true);
  };

  const handleFileIdSubmit = async () => {
    try {
      setLoading(true);
      await studentService.updateStudent(currentStudent.objectId, {
        documented: true,
        fileId: fileId,
        documentedAt: new Date().toISOString()
      });
      message.success('审档通过');
      setFileIdModalVisible(false);
      setFileId('');
      fetchUndocumentedStudents();
    } catch (error) {
      message.error('操作失败');
    } finally {
      setLoading(false);
    }
  };

  // Handle documentation rejection
  const handleReject = (student) => {
    setCurrentStudent(student);
    setRejectModalVisible(true);
  };

  const handleRejectSubmit = async () => {
    try {
      setLoading(true);
      // Add rejection note to student's notes array
      const notes = currentStudent.notes || [];
      notes.push({
        content: `审档不通过：${rejectNote}`,
        date: new Date().toISOString()
      });
      
      await studentService.updateStudent(currentStudent.objectId, {
        notes: notes,
        documented: false
      });
      message.success('已记录审档不通过原因');
      setRejectModalVisible(false);
      setRejectNote('');
      fetchUndocumentedStudents();
    } catch (error) {
      message.error('操作失败');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '身份证号',
      dataIndex: 'uniqueId',
      key: 'uniqueId',
    },
    {
      title: '报名日期',
      dataIndex: 'signUpDate',
      key: 'signUpDate',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button type="primary" onClick={() => handleApprove(record)}>
            审档通过
          </Button>
          <Button danger onClick={() => handleReject(record)}>
            审档不通过
          </Button>
        </Space>
      ),
    },
  ];

  if (!hasPermission) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '50px', 
        fontSize: '16px', 
        color: '#ff4d4f' 
      }}>
        您没有权限访问此页面，请联系管理员
      </div>
    );
  }

  return (
    <div>
      <Table
        columns={columns}
        dataSource={undocumentedStudents}
        loading={loading}
        rowKey="objectId"
      />

      {/* File ID Input Modal */}
      <Modal
        title="输入档案号"
        open={fileIdModalVisible}
        onOk={handleFileIdSubmit}
        onCancel={() => {
          setFileIdModalVisible(false);
          setFileId('');
        }}
        confirmLoading={loading}
      >
        <Input
          placeholder="请输入档案号"
          value={fileId}
          onChange={e => setFileId(e.target.value)}
        />
      </Modal>

      {/* Rejection Note Modal */}
      <Modal
        title="审档不通过原因"
        open={rejectModalVisible}
        onOk={handleRejectSubmit}
        onCancel={() => {
          setRejectModalVisible(false);
          setRejectNote('');
        }}
        confirmLoading={loading}
      >
        <Input.TextArea
          placeholder="请输入审档不通过原因"
          value={rejectNote}
          onChange={e => setRejectNote(e.target.value)}
          rows={4}
        />
      </Modal>
    </div>
  );
};

export default StudentDocumentation; 